/**
 * PaperSessionPlanDrawer — Paper Session Workflow V2.
 *
 * A compact slide-over (NOT a banner) that turns a Markets row into a structured
 * DRAFT paper-session plan: auto-fills intelligence + technical levels, computes
 * risk, validates against the read-only backend validator, and saves/reviews
 * app-local drafts. It NEVER renders Submit/Buy/Sell/Execute and NEVER calls a
 * broker or order endpoint.
 *
 * Opens on window CustomEvent 'adversyn:plan-paper-session' (detail = raw mover)
 * or 'adversyn:open-paper-drafts' (drafts list).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Save, Trash2, FileText, CheckCircle2, ShieldAlert, Loader2, ListChecks, Eye } from "lucide-react";
import { apiV1Base } from "../lib/apiConfig";
import {
  validatePlan, listDrafts, createDraft, patchDraft, deleteDraft,
  computeRisk, localReadiness, getAccountRiskProfiles,
  type PaperDraft, type ValidationResult, type AccountRiskProfile,
} from "../lib/paperSessionDrafts";

const ACCOUNT_OPTIONS = ["Owl Paper", "Compass Paper", "Brain Paper"];

function n(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const x = typeof v === "number" ? v : Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(x) ? x : null;
}
function money(v: number | null): string { return v === null ? "—" : "$" + v.toLocaleString(undefined, { maximumFractionDigits: 2 }); }
function fmt(v: number | null, d = 2): string { return v === null ? "—" : v.toFixed(d); }
function fmtTime(ts?: string | null): string {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return String(ts); }
}

interface PlanForm {
  accountLabel: string; side: string; entry: string; size: string;
  stopLoss: string; target: string; exitRule: string; thesis: string; timeHorizon: string;
}
const EMPTY_FORM: PlanForm = { accountLabel: ACCOUNT_OPTIONS[0], side: "long", entry: "", size: "", stopLoss: "", target: "", exitRule: "", thesis: "", timeHorizon: "Intraday" };

export default function PaperSessionPlanDrawer() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"plan" | "drafts" | "review">("plan");
  const [snap, setSnap] = useState<Record<string, any> | null>(null);
  const [levels, setLevels] = useState<Record<string, any> | null>(null);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [drafts, setDrafts] = useState<PaperDraft[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<AccountRiskProfile[]>([]);
  const [reviewItems, setReviewItems] = useState<PaperDraft[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Derived intelligence from the row snapshot.
  const intel = useMemo(() => {
    const s = snap || {};
    return {
      symbol: String(s.symbol || "").toUpperCase(),
      price: n(s.price ?? s.current_price),
      movePct: n(s.change_percent ?? s.current_move_pct),
      volume: n(s.volume),
      lane: String(s.instrument_class && s.instrument_class !== "standard" ? s.instrument_class : (s.asset_class || "—")),
      catalystStatus: String(s.catalyst_status || "—"),
      catalystHeadline: s.primary_catalyst_display || s.primary_catalyst || null,
      catalystSource: s.primary_catalyst_source || null,
      dqFlags: Array.isArray(s.dq_flags) ? s.dq_flags.map((x: any) => String(x).toUpperCase()) : [],
      band: s.integrity_band ? String(s.integrity_band).toUpperCase() : null,
      status: s.integrity_status ? String(s.integrity_status).toUpperCase() : null,
      score: n(s.integrity_score),
      marketCap: n(s.integrity_market_cap),
      rvol: n(s.relative_volume ?? s.rvol),
      riskBand: String(s.risk_band || s.integrity_band || "").toUpperCase() || null,
    };
  }, [snap]);

  const readiness = useMemo(() => localReadiness({
    side: form.side, entry: form.entry, stop: form.stopLoss, size: form.size,
    exitRule: form.exitRule, dqFlags: intel.dqFlags, integrityBand: intel.riskBand, catalystStatus: intel.catalystStatus,
  }), [form, intel]);

  const risk = useMemo(() => computeRisk({ side: form.side, entry: form.entry, stop: form.stopLoss, size: form.size, target: form.target }), [form]);
  const reviewOnly = readiness.reviewOnly;
  const activeProfile = useMemo(() => {
    const want = String(form.accountLabel || "").toLowerCase();
    return profiles.find((p) => String(p.account_label || "").toLowerCase() === want || String(p.account_id || "").toLowerCase() === want) || null;
  }, [profiles, form.accountLabel]);
  const riskLimit = useMemo(() => {
    const maxPos = activeProfile ? n(activeProfile.max_position_value as any) : null;
    const maxRisk = activeProfile ? n(activeProfile.max_risk_dollars as any) : null;
    const posVal = risk.positionValue;
    const dRisk = risk.maxLoss;
    const posOk = maxPos === null || posVal === null ? null : posVal <= maxPos;
    const riskOk = maxRisk === null || dRisk === null ? null : dRisk <= maxRisk;
    const shortOk = activeProfile && activeProfile.allow_short_watch === false && /short|sell/.test(String(form.side).toLowerCase()) ? false : true;
    const posPct = maxPos && posVal != null ? Math.min(100, (posVal / maxPos) * 100) : null;
    const riskPct = maxRisk && dRisk != null ? Math.min(100, (dRisk / maxRisk) * 100) : null;
    return { maxPos, maxRisk, posVal, dRisk, posOk, riskOk, shortOk, posPct, riskPct, available: !!activeProfile, source: activeProfile?.source_status || null };
  }, [activeProfile, risk, form.side]);

  const refreshDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try { setDrafts(await listDrafts()); } finally { setDraftsLoading(false); }
  }, []);

  const openFromRow = useCallback(async (raw: Record<string, any>) => {
    setSnap(raw);
    setValidation(null);
    setCurrentId(null);
    setStatusMsg(null);
    setLevels(null);
    const sym = String(raw.symbol || "").toUpperCase();
    const rowPrice = n(raw.price ?? raw.current_price);
    const movePct = n(raw.change_percent ?? raw.current_move_pct);
    setForm({ ...EMPTY_FORM, side: movePct !== null && movePct < 0 ? "short" : "long", entry: rowPrice !== null ? String(rowPrice) : "" });
    setMode("plan");
    setOpen(true);
    if (sym) {
      setLevelsLoading(true);
      try {
        const r = await fetch(`${apiV1Base()}/ta/levels/${encodeURIComponent(sym)}`, { cache: "no-store" });
        if (r.ok) {
          const b = await r.json();
          const d = b?.data ?? b;
          setLevels(d);
          // If entry still equals the row price (untouched), prefer the technical latest price.
          const latest = n(d?.latest_price);
          if (latest !== null) {
            setForm((f) => (f.entry === (rowPrice !== null ? String(rowPrice) : "") ? { ...f, entry: String(latest) } : f));
          }
        }
      } catch { /* honest: levels just stay null */ } finally { setLevelsLoading(false); }
    }
  }, []);

  useEffect(() => {
    const onPlan = (e: Event) => {
      const detail = (e as CustomEvent).detail as Record<string, any> | undefined;
      if (detail && detail.symbol) void openFromRow(detail);
    };
    const onDrafts = () => { setMode("drafts"); setOpen(true); void refreshDrafts(); };
    window.addEventListener("adversyn:plan-paper-session", onPlan);
    window.addEventListener("adversyn:open-paper-drafts", onDrafts);
    return () => {
      window.removeEventListener("adversyn:plan-paper-session", onPlan);
      window.removeEventListener("adversyn:open-paper-drafts", onDrafts);
    };
  }, [openFromRow, refreshDrafts]);

  useEffect(() => { if (open && mode === "drafts") void refreshDrafts(); }, [open, mode, refreshDrafts]);
  const refreshReview = useCallback(async () => {
    setReviewLoading(true);
    try { setReviewItems(await listDrafts("ready_for_owner_review")); } finally { setReviewLoading(false); }
  }, []);
  useEffect(() => { if (open && mode === "review") void refreshReview(); }, [open, mode, refreshReview]);
  useEffect(() => { if (open && profiles.length === 0) void getAccountRiskProfiles().then(setProfiles).catch(() => { }); }, [open, profiles.length]);

  const planPayload = useCallback(() => ({
    symbol: intel.symbol,
    account_label: form.accountLabel,
    account_id: form.accountLabel,
    side: form.side,
    entry: n(form.entry),
    size: n(form.size),
    stop_loss: n(form.stopLoss),
    target: n(form.target),
    exit_rule: form.exitRule,
    thesis: form.thesis,
    time_horizon: form.timeHorizon,
    source_module: "markets",
    source_component: (snap && (snap as any).__source_component) || null,
    source_row_snapshot: {
      symbol: intel.symbol, price: intel.price, change_percent: intel.movePct, volume: intel.volume,
      lane: intel.lane, dq_flags: intel.dqFlags, signal_use_display: snap?.signal_use_display ?? null,
    },
    technical_snapshot: levels || null,
    integrity_snapshot: {
      integrity_band: intel.band, integrity_status: intel.status, integrity_score: intel.score,
      integrity_market_cap: intel.marketCap, risk_band: intel.riskBand, rvol: intel.rvol,
    },
    catalyst_snapshot: {
      catalyst_status: intel.catalystStatus, headline: intel.catalystHeadline, source: intel.catalystSource,
    },
  }), [intel, form, levels, snap]);

  const onValidate = useCallback(async () => {
    setValidating(true);
    setStatusMsg(null);
    try {
      const p = planPayload();
      const res = await validatePlan({
        symbol: p.symbol, side: p.side, proposed_price: p.entry, stop_loss: p.stop_loss,
        size: p.size, exit_rule: p.exit_rule, target: p.target, dq_flags: p.source_row_snapshot.dq_flags,
        catalyst_status: p.catalyst_snapshot.catalyst_status, risk_band: p.integrity_snapshot.risk_band,
        account_id: p.account_label,
      } as Record<string, unknown>);
      setValidation(res);
    } catch (e: any) {
      setStatusMsg(`Validation unavailable (${String(e?.message || e)}).`);
    } finally { setValidating(false); }
  }, [planPayload]);

  const onSave = useCallback(async () => {
    setStatusMsg(null);
    const payload = planPayload();
    try {
      let saved: PaperDraft | null;
      if (currentId) saved = await patchDraft(currentId, payload);
      else saved = await createDraft(payload);
      if (saved) {
        setCurrentId(saved.draft_id);
        setValidation((v) => v); // keep
        setStatusMsg(`Draft saved (${saved.validation_status === "ready_for_owner_review" ? "ready for review" : "blocked"}).`);
        void refreshDrafts();
      }
    } catch (e: any) { setStatusMsg(`Save failed (${String(e?.message || e)}).`); }
  }, [planPayload, currentId, refreshDrafts]);

  const onMarkReviewed = useCallback(async () => {
    if (!currentId) { setStatusMsg("Save the draft before marking reviewed."); return; }
    const updated = await patchDraft(currentId, { review_status: "reviewed" });
    if (updated) { setStatusMsg("Marked reviewed."); void refreshDrafts(); }
  }, [currentId, refreshDrafts]);

  const onMarkReviewedId = useCallback(async (id: string) => {
    const u = await patchDraft(id, { review_status: "reviewed" });
    if (u) void refreshReview();
  }, [refreshReview]);

  const onDeleteReview = useCallback(async (id: string) => {
    await deleteDraft(id);
    void refreshReview();
  }, [refreshReview]);

  const onClear = useCallback(() => {
    setForm((f) => ({ ...EMPTY_FORM, accountLabel: f.accountLabel, side: f.side }));
    setValidation(null);
    setStatusMsg("Cleared.");
  }, []);

  const onExport = useCallback(() => {
    const p = planPayload();
    const lines = [
      `PAPER SESSION PLAN — ${p.symbol}`,
      `Generated: ${new Date().toISOString()}`,
      "",
      `Account (label only): ${p.account_label}`,
      `Side: ${p.side}    Time horizon: ${p.time_horizon}`,
      `Entry: ${p.entry ?? "—"}   Stop: ${p.stop_loss ?? "—"}   Target: ${p.target ?? "—"}   Size: ${p.size ?? "—"}`,
      `Exit rule: ${p.exit_rule || "—"}`,
      `Risk/share: ${fmt(risk.riskPerShare)}   $ risk (max loss): ${money(risk.maxLoss)}   R:R ${risk.rewardRiskRatio !== null ? risk.rewardRiskRatio.toFixed(2) : "—"}`,
      `Position value: ${money(risk.positionValue)}`,
      "",
      `Integrity: band ${intel.band || "—"} / status ${intel.status || "—"} / score ${intel.score ?? "—"} / mkt cap ${money(intel.marketCap)}`,
      `Catalyst: ${intel.catalystStatus}${intel.catalystHeadline ? " — " + intel.catalystHeadline : ""}`,
      `DQ flags: ${intel.dqFlags.length ? intel.dqFlags.join(", ") : "none"}`,
      `RVOL: ${intel.rvol ?? "—"}`,
      "",
      `Thesis: ${p.thesis || "—"}`,
      "",
      `Validation: ${validation ? validation.status : "(not validated)"}`,
      ...(validation?.reasons || []).map((r) => `  - ${r}`),
      "",
      "NOTE: Draft only. No order placed. Owner authorization required before any future submission.",
    ];
    try {
      const blob = new Blob([lines.join("\n")], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `paper-plan-${p.symbol || "draft"}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setStatusMsg("Notes exported.");
    } catch (e: any) { setStatusMsg(`Export failed (${String(e?.message || e)}).`); }
  }, [planPayload, risk, intel, validation]);

  const loadDraft = useCallback((d: PaperDraft) => {
    const integ = (d.integrity_snapshot || {}) as Record<string, any>;
    const cat = (d.catalyst_snapshot || {}) as Record<string, any>;
    const srow = (d.source_row_snapshot || {}) as Record<string, any>;
    setSnap({
      symbol: d.symbol, price: srow.price, change_percent: srow.change_percent, volume: srow.volume,
      asset_class: srow.lane, dq_flags: srow.dq_flags || [], integrity_band: integ.integrity_band,
      integrity_status: integ.integrity_status, integrity_score: integ.integrity_score,
      integrity_market_cap: integ.integrity_market_cap, risk_band: integ.risk_band,
      relative_volume: integ.rvol, catalyst_status: cat.catalyst_status,
      primary_catalyst_display: cat.headline, primary_catalyst_source: cat.source,
    });
    setLevels((d.technical_snapshot as Record<string, any>) || null);
    setForm({
      accountLabel: d.account_label || ACCOUNT_OPTIONS[0], side: d.side || "long",
      entry: d.entry != null ? String(d.entry) : "", size: d.size != null ? String(d.size) : "",
      stopLoss: d.stop_loss != null ? String(d.stop_loss) : "", target: d.target != null ? String(d.target) : "",
      exitRule: d.exit_rule || "", thesis: d.thesis || "", timeHorizon: d.time_horizon || "Intraday",
    });
    setCurrentId(d.draft_id);
    setValidation(null);
    setMode("plan");
    setStatusMsg(`Loaded draft ${d.symbol}.`);
  }, []);

  useEffect(() => {
    const onOpenDraft = (e: Event) => {
      const d = (e as CustomEvent).detail as PaperDraft | undefined;
      if (d && (d as any).draft_id) { loadDraft(d); setOpen(true); }
    };
    window.addEventListener("adversyn:open-paper-draft", onOpenDraft);
    return () => window.removeEventListener("adversyn:open-paper-draft", onOpenDraft);
  }, [loadDraft]);

  const onDeleteDraft = useCallback(async (id: string) => {
    await deleteDraft(id);
    if (currentId === id) setCurrentId(null);
    void refreshDrafts();
  }, [currentId, refreshDrafts]);

  if (!open) return null;

  const bandTone = (b: string | null) => {
    switch (b) {
      case "CLEAR": return "text-emerald-600 border-emerald-500/40";
      case "CAUTION": return "text-amber-600 border-amber-500/40";
      case "HIGH_RISK": return "text-orange-600 border-orange-500/40";
      case "DO_NOT_TRADE": return "text-rose-600 border-rose-500/40";
      default: return "text-[var(--muted-text)] border-[var(--card-border)]";
    }
  };

  const setF = (k: keyof PlanForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full px-2 py-1.5 rounded-lg border border-[var(--card-border)] bg-[var(--input-bg)] text-sm text-[var(--app-text)] disabled:opacity-50";
  const labelCls = "text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1 block";

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="paper-plan-drawer">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" onClick={() => setOpen(false)} data-testid="paper-plan-scrim" />
      <div className="relative h-full w-full max-w-md bg-[var(--card-bg)] border-l border-[var(--card-border)] shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-[var(--card-border)] bg-[var(--card-bg)]">
          <div className="flex items-center gap-2">
            <ListChecks size={16} className="text-[var(--muted-text)]" />
            <span className="text-sm font-black uppercase italic text-[var(--app-text)]">Paper Session Plan</span>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="paper-plan-tab-plan" onClick={() => setMode("plan")} className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest ${mode === "plan" ? "bg-[var(--app-text)] text-[var(--card-bg)]" : "text-[var(--muted-text)]"}`}>Plan</button>
            <button data-testid="paper-plan-tab-drafts" onClick={() => { setMode("drafts"); void refreshDrafts(); }} className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest ${mode === "drafts" ? "bg-[var(--app-text)] text-[var(--card-bg)]" : "text-[var(--muted-text)]"}`}>Drafts</button>
            <button data-testid="paper-plan-tab-review" onClick={() => { setMode("review"); void refreshReview(); }} className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest ${mode === "review" ? "bg-[var(--app-text)] text-[var(--card-bg)]" : "text-[var(--muted-text)]"}`}>Review</button>
            <button data-testid="paper-plan-btn-close" onClick={() => setOpen(false)} title="Close" className="ml-1 p-1 rounded hover:bg-[var(--input-bg)] text-[var(--muted-text)]"><X size={16} /></button>
          </div>
        </div>

        {mode === "review" ? (
          <div className="p-4 space-y-3" data-testid="paper-plan-review-queue">
            {reviewLoading ? (
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] py-6 text-center">Loading review queue…</div>
            ) : reviewItems.length === 0 ? (
              <div data-testid="paper-plan-review-empty" className="text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] py-6 text-center rounded-xl border border-dashed border-[var(--card-border)]">No drafts ready for Owner review.</div>
            ) : reviewItems.map((d) => {
              const integ = (d.integrity_snapshot || {}) as Record<string, any>;
              const cat = (d.catalyst_snapshot || {}) as Record<string, any>;
              const rk = computeRisk({ side: d.side, entry: d.entry, stop: d.stop_loss, size: d.size, target: d.target });
              return (
                <div key={d.draft_id} data-testid="paper-plan-review-row" data-symbol={d.symbol} className="rounded-xl border border-[var(--card-border)] p-3 bg-[var(--input-bg)]/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black italic text-[var(--app-text)]">{d.symbol}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{d.side || "—"}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Ready · Within limit</span>
                      {d.review_status === "reviewed" && <span className="text-[9px] font-black uppercase tracking-widest text-[var(--app-text)]">· Reviewed</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid="paper-plan-review-open" onClick={() => loadDraft(d)} title="Open" className="p-1 rounded hover:bg-[var(--card-border)]/40 text-[var(--muted-text)]"><Eye size={14} /></button>
                      <button data-testid="paper-plan-review-mark" onClick={() => void onMarkReviewedId(d.draft_id)} title="Mark reviewed" className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600"><CheckCircle2 size={14} /></button>
                      <button data-testid="paper-plan-review-delete" onClick={() => void onDeleteReview(d.draft_id)} title="Delete" className="p-1 rounded hover:bg-rose-500/10 text-rose-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    <span>Account: {d.account_label || "—"}</span>
                    <span>Risk: {money(rk.maxLoss)}</span>
                    <span>Band: {integ.integrity_band || "—"}</span>
                    <span>Catalyst: {cat.catalyst_status || "—"}</span>
                    <span className="col-span-2 normal-case tracking-normal">Updated: {fmtTime(d.updated_ts)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : mode === "drafts" ? (
          <div className="p-4 space-y-3" data-testid="paper-plan-drafts-list">
            {draftsLoading ? (
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] py-6 text-center">Loading drafts…</div>
            ) : drafts.length === 0 ? (
              <div data-testid="paper-plan-drafts-empty" className="text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] py-6 text-center rounded-xl border border-dashed border-[var(--card-border)]">No saved paper-session drafts yet.</div>
            ) : drafts.map((d) => {
              const integ = (d.integrity_snapshot || {}) as Record<string, any>;
              const cat = (d.catalyst_snapshot || {}) as Record<string, any>;
              const rk = computeRisk({ side: d.side, entry: d.entry, stop: d.stop_loss, size: d.size, target: d.target });
              return (
                <div key={d.draft_id} data-testid="paper-plan-draft-row" data-symbol={d.symbol} className="rounded-xl border border-[var(--card-border)] p-3 bg-[var(--input-bg)]/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black italic text-[var(--app-text)]">{d.symbol}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{d.side || "—"}</span>
                      {d.review_status === "reviewed" && <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">Reviewed</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid="paper-plan-draft-open" onClick={() => loadDraft(d)} title="Open" className="p-1 rounded hover:bg-[var(--card-border)]/40 text-[var(--muted-text)]"><Eye size={14} /></button>
                      <button data-testid="paper-plan-draft-delete" onClick={() => void onDeleteDraft(d.draft_id)} title="Delete" className="p-1 rounded hover:bg-rose-500/10 text-rose-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    <span>Status: {d.validation_status === "ready_for_owner_review" ? "Ready for review" : (d.validation_status || "—")}</span>
                    <span>Risk: {money(rk.maxLoss)}</span>
                    <span>Band: {integ.integrity_band || "—"}</span>
                    <span>Catalyst: {cat.catalyst_status || "—"}</span>
                    <span className="col-span-2 normal-case tracking-normal">Updated: {fmtTime(d.updated_ts)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Intelligence summary */}
            <div className="rounded-xl border border-[var(--card-border)] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span data-testid="paper-plan-symbol" className="text-lg font-black italic text-[var(--app-text)]">{intel.symbol || "—"}</span>
                  {intel.symbol && (
                    /* ADV-UIACCESS: open the same stock-detail chart for this symbol. */
                    <button
                      type="button"
                      data-testid="paper-plan-open-chart"
                      onClick={() => { try { window.dispatchEvent(new CustomEvent('adversyn:symbol-selected', { detail: { value: intel.symbol } })); } catch { /* ignore */ } setOpen(false); }}
                      title={`Open ${intel.symbol} chart`}
                      aria-label={`Open ${intel.symbol} chart`}
                      className="px-2 py-0.5 rounded border border-[var(--card-border)] text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
                    >
                      Open Chart
                    </button>
                  )}
                </div>
                {intel.band && (
                  <span data-testid="paper-plan-integrity-band" className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${bandTone(intel.band)}`}>{intel.band.replace(/_/g, " ")}{intel.score !== null ? ` · ${intel.score}` : ""}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                <span>Price: {money(intel.price)}</span>
                <span>Move: {intel.movePct !== null ? intel.movePct.toFixed(2) + "%" : "—"}</span>
                <span>Lane: {intel.lane}</span>
                <span>RVOL: {intel.rvol ?? "—"}</span>
                <span>Mkt cap: {money(intel.marketCap)}</span>
                <span>Vol: {intel.volume !== null ? intel.volume.toLocaleString() : "—"}</span>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest" data-testid="paper-plan-catalyst-status">
                <span className="text-[var(--muted-text)]">Catalyst: </span>
                <span className="text-[var(--app-text)]">{intel.catalystStatus}</span>
              </div>
              {intel.catalystHeadline && <div className="text-[10px] text-[var(--muted-text)] normal-case tracking-normal">{String(intel.catalystHeadline)}{intel.catalystSource ? ` (${intel.catalystSource})` : ""}</div>}
              {intel.dqFlags.length > 0 && (
                <div data-testid="paper-plan-dq-flags" className="text-[10px] font-black uppercase tracking-widest text-amber-600">DQ: {intel.dqFlags.join(", ")}</div>
              )}
            </div>

            {/* Technical levels */}
            <div className="rounded-xl border border-[var(--card-border)] p-3" data-testid="paper-plan-levels">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">Technical Levels (advisory)</span>
                {levelsLoading && <Loader2 size={12} className="animate-spin text-[var(--muted-text)]" />}
              </div>
              {levels && !levels.degraded ? (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                  <span>Latest: {money(n(levels.latest_price))}</span>
                  <span>VWAP: {money(n(levels.vwap))}</span>
                  <span>PD High: {money(n(levels.prior_day_high))}</span>
                  <span>PD Low: {money(n(levels.prior_day_low))}</span>
                  <span>Range floor: {money(n(levels.range_floor))}</span>
                  <span>Range ceil: {money(n(levels.range_ceiling))}</span>
                  <span>Breakout: {money(n(levels.breakout_trigger))}</span>
                  <span>Invalidation: {money(n(levels.invalidation_level))}</span>
                </div>
              ) : (
                <div data-testid="paper-plan-levels-unavailable" className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{levelsLoading ? "Loading levels…" : "Technical levels unavailable for this symbol."}</div>
              )}
              {levels && !levels.degraded && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {n(levels.invalidation_level) !== null && <button type="button" disabled={reviewOnly} onClick={() => setForm((f) => ({ ...f, stopLoss: String(levels.invalidation_level) }))} className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] disabled:opacity-40">Stop ← invalidation</button>}
                  {n(levels.breakout_trigger) !== null && <button type="button" disabled={reviewOnly} onClick={() => setForm((f) => ({ ...f, target: String(levels.breakout_trigger) }))} className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] disabled:opacity-40">Target ← breakout</button>}
                </div>
              )}
            </div>

            {reviewOnly && (
              <div data-testid="paper-plan-reviewonly" className="flex items-start gap-2 text-[10px] font-bold text-rose-600 px-1">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                <span>Review only — this setup is blocked ({readiness.blockers.join(" ")}). No plan can be marked ready for review.</span>
              </div>
            )}

            {/* Plan inputs */}
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelCls}>Account</label><select data-testid="paper-plan-field-account" className={inputCls} value={form.accountLabel} onChange={setF("accountLabel")} disabled={reviewOnly}>{ACCOUNT_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
              <div><label className={labelCls}>Side</label><select data-testid="paper-plan-field-side" className={inputCls} value={form.side} onChange={setF("side")} disabled={reviewOnly}><option value="long">Long</option><option value="short">Short-watch</option></select></div>
              <div><label className={labelCls}>Entry</label><input data-testid="paper-plan-field-entry" className={inputCls} value={form.entry} onChange={setF("entry")} disabled={reviewOnly} inputMode="decimal" /></div>
              <div><label className={labelCls}>Size</label><input data-testid="paper-plan-field-size" className={inputCls} value={form.size} onChange={setF("size")} disabled={reviewOnly} inputMode="decimal" /></div>
              <div><label className={labelCls}>Stop-loss</label><input data-testid="paper-plan-field-stop" className={inputCls} value={form.stopLoss} onChange={setF("stopLoss")} disabled={reviewOnly} inputMode="decimal" /></div>
              <div><label className={labelCls}>Target</label><input data-testid="paper-plan-field-target" className={inputCls} value={form.target} onChange={setF("target")} disabled={reviewOnly} inputMode="decimal" /></div>
              <div className="col-span-2"><label className={labelCls}>Exit rule</label><input data-testid="paper-plan-field-exit" className={inputCls} value={form.exitRule} onChange={setF("exitRule")} disabled={reviewOnly} placeholder="e.g. close below VWAP / time stop 15:55" /></div>
              <div><label className={labelCls}>Time horizon</label><input data-testid="paper-plan-field-horizon" className={inputCls} value={form.timeHorizon} onChange={setF("timeHorizon")} disabled={reviewOnly} /></div>
              <div className="col-span-2"><label className={labelCls}>Thesis</label><textarea data-testid="paper-plan-field-thesis" className={inputCls + " min-h-[52px]"} value={form.thesis} onChange={setF("thesis")} disabled={reviewOnly} /></div>
            </div>

            {/* Account risk limits (V3) */}
            <div className="rounded-xl border border-[var(--card-border)] p-3" data-testid="paper-plan-account-risk">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">Account Risk — {form.accountLabel}</span>
                {riskLimit.available ? (
                  (riskLimit.posOk === false || riskLimit.riskOk === false || riskLimit.shortOk === false) ? (
                    <span data-testid="paper-plan-risk-limit-status" data-ok="false" className="text-[9px] font-black uppercase tracking-widest text-rose-600 inline-flex items-center gap-1"><ShieldAlert size={11} /> Over limit</span>
                  ) : (
                    <span data-testid="paper-plan-risk-limit-status" data-ok="true" className="text-[9px] font-black uppercase tracking-widest text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Within limit</span>
                  )
                ) : (
                  <span data-testid="paper-plan-risk-limit-status" data-ok="unknown" className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">Risk limit unavailable — manual review</span>
                )}
              </div>
              {riskLimit.available && (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                    <span>Max pos: {money(riskLimit.maxPos)}</span>
                    <span>Plan pos: <span data-testid="paper-plan-plan-posval" className={riskLimit.posOk === false ? "text-rose-600" : "text-[var(--app-text)]"}>{money(riskLimit.posVal)}</span></span>
                    <span>Max risk: {money(riskLimit.maxRisk)}</span>
                    <span>Plan risk: <span data-testid="paper-plan-plan-risk" className={riskLimit.riskOk === false ? "text-rose-600" : "text-[var(--app-text)]"}>{money(riskLimit.dRisk)}</span></span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {riskLimit.posPct !== null && (
                      <div className="flex items-center gap-2"><span className="text-[8px] uppercase tracking-widest text-[var(--muted-text)] w-10">Pos</span><div className="flex-1 h-1.5 rounded-full bg-[var(--input-bg)] overflow-hidden"><div className={`h-full ${riskLimit.posOk === false ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${riskLimit.posPct}%` }} /></div></div>
                    )}
                    {riskLimit.riskPct !== null && (
                      <div className="flex items-center gap-2"><span className="text-[8px] uppercase tracking-widest text-[var(--muted-text)] w-10">Risk</span><div className="flex-1 h-1.5 rounded-full bg-[var(--input-bg)] overflow-hidden"><div className={`h-full ${riskLimit.riskOk === false ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${riskLimit.riskPct}%` }} /></div></div>
                    )}
                  </div>
                  {riskLimit.shortOk === false && <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-rose-600">Short-watch disabled on this account.</div>}
                  {riskLimit.source && <div className="mt-1 text-[8px] font-bold uppercase tracking-widest text-[var(--muted-text)]">Source: {riskLimit.source}</div>}
                </>
              )}
            </div>

            {/* Risk calculator */}
            <div className="rounded-xl border border-[var(--card-border)] p-3" data-testid="paper-plan-risk">
              <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-2">Risk Calculator</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                <span>Risk/share: <span data-testid="paper-plan-risk-per-share" className="text-[var(--app-text)]">{fmt(risk.riskPerShare)}</span></span>
                <span>$ risk: <span data-testid="paper-plan-risk-dollar" className="text-[var(--app-text)]">{money(risk.dollarRisk)}</span></span>
                <span>Pos. value: <span data-testid="paper-plan-risk-position-value" className="text-[var(--app-text)]">{money(risk.positionValue)}</span></span>
                <span>Max loss: <span data-testid="paper-plan-risk-max-loss" className="text-[var(--app-text)]">{money(risk.maxLoss)}</span></span>
                <span>Reward/share: <span className="text-[var(--app-text)]">{fmt(risk.rewardPerShare)}</span></span>
                <span>R:R: <span data-testid="paper-plan-risk-rr" className="text-[var(--app-text)]">{risk.rewardRiskRatio !== null ? risk.rewardRiskRatio.toFixed(2) : "—"}</span></span>
              </div>
              {risk.stopDirectionWarning && (
                <div data-testid="paper-plan-stop-direction-warning" className="mt-2 text-[10px] font-black uppercase tracking-widest text-rose-600">{risk.stopDirectionWarning}</div>
              )}
            </div>

            {/* Validation */}
            <div className="rounded-xl border border-[var(--card-border)] p-3" data-testid="paper-plan-validation">
              {validation ? (
                <>
                  {validation.status === "ready_for_owner_review" ? (
                    <div data-testid="paper-plan-readiness" className="flex items-center gap-2 text-emerald-600 text-xs font-black uppercase tracking-widest"><CheckCircle2 size={14} /> Ready for Owner review</div>
                  ) : (
                    <div data-testid="paper-plan-validation-blocked" className="text-rose-600 text-xs font-black uppercase tracking-widest">Blocked — not ready</div>
                  )}
                  {(validation.reasons || []).length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[10px] text-[var(--muted-text)] list-disc pl-4">
                      {validation.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                  <div className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">Submission: blocked · Owner authorization required · Live trading: disabled</div>
                </>
              ) : (
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                  {readiness.blockers.length ? `Preview blockers: ${readiness.blockers.join(" ")}` : "Plan looks complete — run Validate Plan for the authoritative verdict."}
                </div>
              )}
            </div>

            {statusMsg && <div data-testid="paper-plan-status-msg" className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{statusMsg}</div>}

            {/* Actions (no submit/buy/sell/execute, ever) */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid="paper-plan-btn-validate" onClick={() => void onValidate()} disabled={validating} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-[var(--app-text)] text-[var(--card-bg)] disabled:opacity-50">
                {validating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Validate Plan
              </button>
              <button data-testid="paper-plan-btn-save" onClick={() => void onSave()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--app-text)] hover:bg-[var(--input-bg)]"><Save size={12} /> Save Draft</button>
              <button data-testid="paper-plan-btn-mark-reviewed" onClick={() => void onMarkReviewed()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:bg-[var(--input-bg)]"><CheckCircle2 size={12} /> Mark Reviewed</button>
              <button data-testid="paper-plan-btn-export" onClick={onExport} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:bg-[var(--input-bg)]"><FileText size={12} /> Export Notes</button>
              <button data-testid="paper-plan-btn-clear" onClick={onClear} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:bg-[var(--input-bg)]">Clear</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
